// frontend/src/pages/PettyCash.js
import React, { useEffect, useState, useCallback } from 'react';
import { DollarSign, ExternalLink, Loader2, LogIn, AlertTriangle, BookOpen } from 'lucide-react';
import toast from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { useClaimEligibility } from '../hooks/useClaimEligibility';
import { Link } from 'react-router-dom';

/**
 * Petty Cash SSO launch interstitial.
 *
 * Benefit rules (e.g. dental = confirmed staff) live in HR Portal Clause 8.
 * Petty Cash is only the reimbursement channel — we never hard-block launch
 * here (other claim types remain valid during probation). We surface a
 * one-time advisory so unconfirmed staff understand dental is ineligible.
 * Confirmation follows is_confirmed_staff (status + calendar end date), not
 * employment_status alone — a lapsed probation status must not keep the gate.
 */
const PettyCash = () => {
  const { user } = useAuth();
  const [launching, setLaunching] = useState(false);
  const [pettyCashUrl, setPettyCashUrl] = useState(null);
  const [advisoryAcknowledged, setAdvisoryAcknowledged] = useState(false);

  // JWT /me carries is_confirmed_staff. Employment status can lag after
  // the probation end date — never gate on status alone.
  const jwtConfirmed = user?.is_confirmed_staff === true;

  const { data: eligibility, isLoading: eligibilityLoading } = useClaimEligibility({
    enabled: Boolean(user) && !jwtConfirmed,
  });

  const dentalAdvisory = eligibility?.advisories?.find(
    (a) => a.code === 'dental_requires_confirmation',
  );

  const isConfirmed = jwtConfirmed || eligibility?.is_confirmed_staff === true;

  // Unconfirmed staff see the Clause 8 advisory once before SSO handoff.
  // Confirmed staff auto-redirect as before.
  const showDentalGate = Boolean(
    user && !isConfirmed && !advisoryAcknowledged && !eligibilityLoading,
  );

  const redirectToPettyCash = useCallback(async () => {
    setLaunching(true);
    try {
      const response = await authAPI.getLaunchCode('petty_cash');
      const { url } = response.data;
      setPettyCashUrl(url);
      window.location.href = url;
    } catch (err) {
      setLaunching(false);
      const message =
        err.response?.data?.error ||
        'Unable to open Petty Cash. Please try again from the sidebar.';
      toast.error(message);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (showDentalGate) return;
    if (eligibilityLoading && !isConfirmed) return;

    let cancelled = false;
    const launch = async () => {
      setLaunching(true);
      try {
        const response = await authAPI.getLaunchCode('petty_cash');
        if (cancelled) return;
        const { url } = response.data;
        setPettyCashUrl(url);
        window.location.href = url;
      } catch (err) {
        if (cancelled) return;
        setLaunching(false);
        const message =
          err.response?.data?.error ||
          'Unable to open Petty Cash. Please try again from the sidebar.';
        toast.error(message);
      }
    };

    launch();
    return () => { cancelled = true; };
  }, [user, showDentalGate, eligibilityLoading, isConfirmed]);

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
          <LogIn className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Login Required
        </h2>
        <p className="text-gray-500 max-w-md">
          Please log in to the HR Portal and open Petty Cash from the sidebar menu.
        </p>
      </div>
    );
  }

  if (showDentalGate && !launching) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-full max-w-lg space-y-5">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
              <DollarSign className="w-8 h-8 text-blue-800" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              Petty Cash Claims
            </h2>
            <p className="text-sm text-gray-500">
              Reimbursement channel for work expenses and medical claims
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left flex gap-3">
            <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                {dentalAdvisory?.title || 'Dental claims during probation'}
              </p>
              <p className="text-sm text-amber-800 mt-1 leading-relaxed">
                {dentalAdvisory?.message || (
                  'Dental reimbursement (Employee Handbook Clause 8) is for confirmed staff only. '
                  + 'You may still submit other petty cash claims. After confirmation, dental '
                  + 'claims are capped at S$150 a year.'
                )}
              </p>
              <Link
                to="/self-service/handbook#medical-sick-hospitalisation"
                className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-blue-800 hover:text-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
              >
                <BookOpen className="w-3.5 h-3.5" />
                View Employee Handbook — Clause 8
              </Link>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => {
                setAdvisoryAcknowledged(true);
                redirectToPettyCash();
              }}
              className="btn btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Continue to Petty Cash
            </button>
          </div>
          <p className="text-xs text-gray-400">
            Outpatient and other eligible claims remain available during probation.
          </p>
        </div>
      </div>
    );
  }

  if (launching) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
          <Loader2 className="w-8 h-8 text-blue-800 animate-spin" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Opening Petty Cash
        </h2>
        <p className="text-gray-500 max-w-md">
          Redirecting you to the Petty Cash application...
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
        <DollarSign className="w-8 h-8 text-blue-800" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Petty Cash Claims
      </h2>
      <p className="text-gray-500 max-w-md mb-6">
        If the redirect didn&apos;t happen automatically, click the button below.
      </p>
      {pettyCashUrl ? (
        <a
          href={pettyCashUrl}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <ExternalLink className="w-4 h-4" />
          Open Petty Cash
        </a>
      ) : (
        <button
          type="button"
          onClick={redirectToPettyCash}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <ExternalLink className="w-4 h-4" />
          Open Petty Cash
        </button>
      )}
    </div>
  );
};

export default PettyCash;
